import React from 'react';
import type { View } from '../../types';
import { MoreIcon } from '../icons';
import { useProgramme } from '../../lib/api/hooks/programme';

interface ProgrammeDetailPageProps {
  programmeId: string;
  setView: (view: View) => void;
}

const ProgrammeDetailPage: React.FC<ProgrammeDetailPageProps> = ({ programmeId, setView }) => {
  const { data: programmeData, isLoading, error } = useProgramme(programmeId);
  const article = programmeData?.data;

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-wine-100"></div>
          <p className="mt-4 text-sm text-gray-500">Loading programme...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="text-center p-8 bg-white rounded-lg">
        <h2 className="text-xl font-bold">Programme not found</h2>
        <p className="text-sm text-gray-500 mt-2">{error?.message || 'The programme could not be found'}</p>
        <button onClick={() => setView({ page: 'Programmes' })} className="mt-4 text-[#681628] hover:underline">
          &larr; Back to Programmes
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-sm text-gray-500">
        <span className="cursor-pointer hover:underline" onClick={() => setView({ page: 'Programmes' })}>Programmes</span>
        <span className="mx-2">&gt;</span>
        <span className="font-medium text-gray-700">{article.title}</span>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-6">
        <div className="flex items-start gap-6">
          {article.coverImage && (
            <img src={article.coverImage} alt={article.title} className="w-40 h-28 object-cover rounded-lg" />
          )}
          <div className="flex-1">
            <div className="flex justify-between items-start">
              <div>
                 <span className="text-xs font-semibold bg-green-100 text-green-700 px-2.5 py-1 rounded-full">
                   {article.status === 'PUBLISH' ? 'Published' : 'Draft'}
                 </span>
                 <h2 className="text-2xl font-bold text-gray-800 mt-2">{article.title}</h2>
              </div>
              <button className="text-gray-500 hover:text-gray-800"><MoreIcon /></button>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Posted {formatDate(article.createdAt)} | Posted by {article.createdBy}
            </p>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4">
            <h3 className="font-semibold text-gray-800 mb-3">Settings</h3>
            <div className="space-y-2">
                <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={article.landingPageAfsaProgramme}
                      disabled
                      className="h-4 w-4 rounded border-gray-300 text-[#681628] focus:ring-[#681628]"
                    />
                    <span className="ml-2 text-sm text-gray-700">Landing Page / AfSA Programme</span>
                </label>
                 <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={article.allowComments}
                      disabled
                      className="h-4 w-4 rounded border-gray-300 text-[#681628] focus:ring-[#681628]"
                    />
                    <span className="ml-2 text-sm text-gray-700">Allow Comments</span>
                </label>
            </div>
        </div>

        <div className="border-t border-gray-200 pt-4">
            <h3 className="font-semibold text-gray-800 mb-3">Cover Image Title</h3>
            <p className="text-sm text-gray-700">{article.coverImageTitle || 'No cover image title'}</p>
        </div>

        <div className="border-t border-gray-200 pt-4">
            <h3 className="font-semibold text-gray-800 mb-3">Article Tags</h3>
            <div className="flex flex-wrap gap-2">
              {Array.isArray(article.articleTag) && article.articleTag.length > 0 ? (
                article.articleTag.map((tag: string, index: number) => (
                  <span key={index} className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full">
                    {tag}
                  </span>
                ))
              ) : (
                <span className="text-sm text-gray-500">No tags</span>
              )}
            </div>
        </div>

        <div className="border-t border-gray-200 pt-4">
            <h3 className="font-semibold text-gray-800 mb-3">Content</h3>
            <div
              className="prose prose-sm max-w-none text-gray-700"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
        </div>
      </div>
    </div>
  );
};

export default ProgrammeDetailPage;
